import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Award, Building2, ChevronRight, UserPlus, Wallet } from 'lucide-react';
import { referralPartnerApi } from '@/api/referralPartner';
import PartnerMilestoneRatesCard, {
  type PartnerMilestoneRatesRow,
} from '@/components/partner/PartnerMilestoneRatesCard';

export default function PartnerDashboard() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['partner-me'],
    queryFn: async () => {
      const res = await referralPartnerApi.me();
      return res.data?.data;
    },
  });

  const points = data?.points;
  const hospitals = data?.partner?.hospitals ?? [];
  const milestoneRates = (data?.milestoneRates ?? []) as PartnerMilestoneRatesRow[];

  const cards = [
    {
      label: 'Available for payout',
      value: points?.availableForPayout ?? 0,
      suffix: 'pts',
      to: '/partner/points',
      gradient: 'from-cyan-500/20 to-cyan-500/5',
      border: 'border-cyan-500/30',
    },
    {
      label: 'Total earned',
      value: points?.totalEarned ?? 0,
      suffix: 'pts',
      to: '/partner/points',
      gradient: 'from-violet-500/20 to-violet-500/5',
      border: 'border-violet-500/30',
    },
    {
      label: 'Paid out',
      value: points?.paid ?? 0,
      suffix: 'pts',
      to: '/partner/payouts',
      gradient: 'from-emerald-500/20 to-emerald-500/5',
      border: 'border-emerald-500/30',
    },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-white">
          Hello, {data?.partner?.firstName ?? 'Partner'}
        </h2>
        <p className="text-slate-400">Track referrals, points, and settlements</p>
      </div>

      {isError && (
        <div className="rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-100/90">
          Failed to load points summary. Please refresh.
          <div className="mt-1 text-xs text-red-200/70">
            {(error as any)?.response?.data?.message || (error as any)?.message || ''}
          </div>
        </div>
      )}

      {isLoading ? (
        <p className="text-slate-400">Loading…</p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-3">
          {cards.map((c) => (
            <Link
              key={c.label}
              to={c.to}
              className={`group rounded-2xl border ${c.border} bg-gradient-to-br ${c.gradient} p-5 transition hover:scale-[1.02]`}
            >
              <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{c.label}</p>
              <p className="mt-2 text-3xl font-bold text-white">
                {c.value}
                <span className="ml-1 text-sm font-normal text-slate-400">{c.suffix}</span>
              </p>
              <ChevronRight className="mt-3 h-4 w-4 text-slate-500 group-hover:text-cyan-400" />
            </Link>
          ))}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Link
          to="/partner/referrals"
          className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/5 p-5 hover:bg-white/10"
        >
          <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-cyan-500/20 text-cyan-400">
            <UserPlus className="h-6 w-6" />
          </span>
          <div className="flex-1">
            <p className="font-semibold text-white">New referral</p>
            <p className="text-sm text-slate-400">Submit a patient with consent</p>
          </div>
          <ChevronRight className="h-5 w-5 text-slate-500" />
        </Link>
        <Link
          to="/partner/payouts"
          className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/5 p-5 hover:bg-white/10"
        >
          <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-violet-500/20 text-violet-400">
            <Wallet className="h-6 w-6" />
          </span>
          <div className="flex-1">
            <p className="font-semibold text-white">Request payout</p>
            <p className="text-sm text-slate-400">Settle approved points</p>
          </div>
          <ChevronRight className="h-5 w-5 text-slate-500" />
        </Link>
      </div>

      {!isLoading && milestoneRates.length > 0 && (
        <PartnerMilestoneRatesCard rates={milestoneRates} />
      )}

      <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-300">
          <Building2 className="h-4 w-4 text-cyan-400" />
          Your hospitals ({hospitals.length})
        </div>
        {hospitals.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500">
            No hospitals linked yet. Contact admin after approval.
          </p>
        ) : (
          <div className="mt-3 flex flex-wrap gap-2">
            {hospitals.map((h: { hospital: { id: string; name: string } }) => (
              <span
                key={h.hospital.id}
                className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-sm text-slate-200"
              >
                {h.hospital.name}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-xl border border-amber-500/20 bg-amber-500/10 px-4 py-3 text-sm text-amber-100/90">
        <Award className="mb-1 inline h-4 w-4" /> Milestones: New referral → OPD visit → IPD admission.
        Points credit automatically when the patient completes each step at the hospital.
        {milestoneRates.length === 0 && !isLoading && (
          <span className="block mt-1 text-xs text-amber-200/70">
            Rate details appear here once your hospital link is active.
          </span>
        )}
      </div>
    </div>
  );
}
